import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, FormControl, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum, PlatformService, PanAppState } from '@panamax/app-state';
import moment from 'moment';
import { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { OrderStatusDetails } from 'src/app/shared/constants/order-status-title.enum';
import { MyOrdersAnalyticsService } from '@app/my-orders/tracking/analytics/my-orders-analytics.service';
import {
  AppliedFilters,
  AppliedFiltersTracking,
} from '@app/my-orders/tracking/analytics/my-orders-applied-filters';
import { DateRange } from '@angular/material/datepicker';
import { FEATURES } from '@shared/constants/splitio-features';
import { DatePreset } from '@app/my-orders/pages/order-status/constants/date-preset.enum';
import { DatePresetService } from '@app/my-orders/pages/order-status/services/date-preset.service';
import { SellerInfo } from './seller.enum';
import { SellerFilterState } from '../services/my-orders-sellers.service';

@Component({
  template: '',
})
export class MyOrdersFiltersBaseComponent implements OnInit, OnDestroy {
  @Input() filters: any = {};
  @Input() filtersFormGroup: UntypedFormGroup;
  @Input() selectedFilter = '';
  @Input() isOptionsComponent = false;
  @Input() sellerFilterState: SellerFilterState = {
    sellerFilterEnabled: false,
    sellers: [],
    showSellerTab: false,
  };

  formValue: any;
  selectedRangeValue: DateRange<Moment> | undefined;
  selectedFiltersSummary: any = { total: 0 };
  searchedCustomers = [];
  minCalendarDate: Moment = moment().startOf('day');
  activePreset: DatePreset | null = null;

  // Store the last navigation date in case calendar isn't ready yet
  protected pendingNavigationDate: Moment | null = null;

  trackSelectedFiltersDetails: AppliedFiltersTracking;
  selectedFilterDetails: AppliedFilters;

  filtersFormSub$: Subscription = new Subscription();
  filterOptionsChangeSubs: { [key: string]: Subscription } = {};

  selectedDateStart: string;
  selectedDateEnd: string;

  features = FEATURES;
  sellerFilterFeatureFlag =
    FEATURES.split_dynamic_order_status_seller_filter.name;

  // Holds the per-category toggle states
  categoryRememberSelections: { [key: string]: boolean } = {
    orderStatus: false,
    customers: false,
    deliveryMethod: false,
    deliveryDate: false,
  };

  // Flags to track if each category has any selected options
  categoryHasSelections: { [key: string]: boolean } = {
    orderStatus: false,
    customers: false,
    deliveryMethod: false,
    deliveryDate: false,
    seller: false,
  };

  // Seller-related properties
  filteredSellers: SellerInfo[] = [];
  searchedSellers: SellerInfo[] = [];
  expandedSellers: { [key: string]: boolean } = {};

  // Allows for the form control to be used in the deliveryDate formgroup without being part of it
  get rememberDeliveryDatePresetControl(): FormControl {
    return this.filtersFormGroup.get(
      'rememberDeliveryDatePreset',
    ) as FormControl;
  }

  dsmInfoSubscription: Subscription;

  constructor(
    protected modalController: ModalController,
    protected translateService: TranslateService,
    readonly platformService: PlatformService,
    private datePipe: DatePipe,
    private myOrdersAnalyticsService: MyOrdersAnalyticsService,
  ) {}

  // Dialogs init and destroy as expected on normal angular apps, no memory leaks
  ngOnInit(): void {
    // Ensure minCalendarDate is exactly at the start of today
    this.minCalendarDate = moment().startOf('day');
    if (this.sellerFilterState?.sellers) {
      this.filteredSellers = this.sellerFilterState.sellers;
      if (this.filteredSellers.length > 0 && this.filteredSellers[0]?.id) {
        this.expandedSellers[this.filteredSellers[0].id] = true;
      } else {
        this.expandedSellers = {};
      }

      if (this.filtersFormGroup) {
        this.initializeSellerFormControls();
      }
    }

    if (this.filtersFormGroup) {
      this.formValue = this.filtersFormGroup.value;
      if (!this.selectedFilter) {
        this.selectedFilter = Object.keys(this.filters)[0] || '';
      }

      // Restore active preset if it exists in the form value
      if (this.formValue?.deliveryDate?.preset) {
        this.activePreset = this.formValue.deliveryDate.preset;
      }

      // Add remember toggle controls for each category - always initialize to false
      if (!this.filtersFormGroup.contains('rememberOrderStatus')) {
        this.filtersFormGroup.addControl(
          'rememberOrderStatus',
          new FormControl(false),
        );
      } else {
        if (
          this.platformService.platformType !== PlatformEnum.mobile ||
          (this.platformService.platformType === PlatformEnum.mobile &&
            !this.isOptionsComponent)
        ) {
          this.filtersFormGroup.get('rememberOrderStatus').setValue(false);
        }
      }

      if (!this.filtersFormGroup.contains('rememberCustomers')) {
        this.filtersFormGroup.addControl(
          'rememberCustomers',
          new FormControl(false),
        );
      } else {
        if (
          this.platformService.platformType !== PlatformEnum.mobile ||
          (this.platformService.platformType === PlatformEnum.mobile &&
            !this.isOptionsComponent)
        ) {
          this.filtersFormGroup.get('rememberCustomers').setValue(false);
        }
      }

      // Add save as default preset toggle control
      if (!this.filtersFormGroup.contains('rememberDeliveryDatePreset')) {
        this.filtersFormGroup.addControl(
          'rememberDeliveryDatePreset',
          new FormControl(false),
        );
      }

      if (!this.filtersFormGroup.contains('rememberDeliveryMethod')) {
        this.filtersFormGroup.addControl(
          'rememberDeliveryMethod',
          new FormControl(false),
        );
      } else {
        if (
          this.platformService.platformType !== PlatformEnum.mobile ||
          (this.platformService.platformType === PlatformEnum.mobile &&
            !this.isOptionsComponent)
        ) {
          this.filtersFormGroup.get('rememberDeliveryMethod').setValue(false);
        }
      }

      // Set up subscriptions to track option selections in each category
      this.setupCategorySelectionTracking();

      // set the value on first load
      this.updateSelectedFiltersSummary(this.filtersFormGroup.value);

      // update it on every change
      this.filtersFormSub$ = this.filtersFormGroup.valueChanges.subscribe(
        value => {
          this.updateSelectedFiltersSummary(value);
        },
      );

      if (
        this.filtersFormGroup.value.deliveryDate &&
        this.filtersFormGroup.value.deliveryDate.start &&
        this.filtersFormGroup.value.deliveryDate.end
      ) {
        this.selectedRangeValue = new DateRange(
          moment(this.filtersFormGroup.value.deliveryDate.start, 'MM-DD-YYYY'),
          moment(this.filtersFormGroup.value.deliveryDate.end, 'MM-DD-YYYY'),
        );
        if (
          this.filtersFormGroup.value.deliveryDate &&
          this.filtersFormGroup.value.deliveryDate.start &&
          this.filtersFormGroup.value.deliveryDate.end
        ) {
          this.selectedRangeValue = new DateRange(
            moment(this.filtersFormGroup.value.deliveryDate.start),
            moment(this.filtersFormGroup.value.deliveryDate.end),
          );
        }
      }
    }
    this.searchedCustomers = this.filters['customers']?.value || [];

    // Initialize toggle as disabled if no date range is set or preset is active
    this.updateSaveAsDefaultToggleState();

    // Initialize searchedSellers with all sellers
    this.searchedSellers = this.filteredSellers || [];
  }

  ngOnDestroy() {
    if (this.filtersFormSub$) {
      this.filtersFormSub$.unsubscribe();
    }

    // Unsubscribe from all category option change subscriptions
    Object.values(this.filterOptionsChangeSubs).forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });

    if (this.dsmInfoSubscription) {
      this.dsmInfoSubscription.unsubscribe();
    }
  }

  // Set up tracking for option selections in each category
  setupCategorySelectionTracking() {
    const categories = ['orderStatus', 'customers', 'deliveryMethod'];

    categories.forEach(category => {
      if (this.filtersFormGroup.get(category)) {
        // Track changes to the category's selections
        this.filterOptionsChangeSubs[category] = this.filtersFormGroup
          .get(category)
          .valueChanges.subscribe(value => {
            const hasSelections =
              this.flattenToArr(value).filter(x => x).length > 0;
            this.categoryHasSelections[category] = hasSelections;

            // If no selections in this category, force the remember toggle off
            if (!hasSelections) {
              const toggleName = `remember${this.capitalizeFirstLetter(category)}`;
              if (this.filtersFormGroup.get(toggleName)) {
                this.filtersFormGroup.get(toggleName).setValue(false);
                this.filtersFormGroup.get(toggleName).disable();
              }
            } else {
              // Enable the toggle if it's disabled
              const toggleName = `remember${this.capitalizeFirstLetter(category)}`;
              if (
                this.filtersFormGroup.get(toggleName) &&
                this.filtersFormGroup.get(toggleName).disabled
              ) {
                this.filtersFormGroup.get(toggleName).enable();
              }
            }
          });

        // Initial check for each category
        const hasSelections =
          this.flattenToArr(this.filtersFormGroup.get(category).value).filter(
            x => x,
          ).length > 0;
        this.categoryHasSelections[category] = hasSelections;

        // Initial toggle state based on selections
        const toggleName = `remember${this.capitalizeFirstLetter(category)}`;
        if (!hasSelections && this.filtersFormGroup.get(toggleName)) {
          this.filtersFormGroup.get(toggleName).setValue(false);
          this.filtersFormGroup.get(toggleName).disable();
        } else if (
          hasSelections &&
          this.filtersFormGroup.get(toggleName) &&
          this.filtersFormGroup.get(toggleName).disabled
        ) {
          this.filtersFormGroup.get(toggleName).enable();
        }

        // Don't automatically set remember toggle when checkboxes are already checked
        // Always keep toggles off by default when opening modal
        if (this.filtersFormGroup.get(toggleName)) {
          // Only reset toggle values in these cases:
          // 1. On desktop/tablet (not mobile)
          // 2. On mobile, but only in the main filters component (not the options component)
          if (
            this.platformService.platformType !== PlatformEnum.mobile ||
            (this.platformService.platformType === PlatformEnum.mobile &&
              !this.isOptionsComponent)
          ) {
            this.filtersFormGroup.get(toggleName).setValue(false);
          }
        }
      }
    });
  }

  capitalizeFirstLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  // Helper to check if a category has any selected options
  hasCategorySelections(category: string): boolean {
    return this.categoryHasSelections[category] || false;
  }

  setSelectedFilter = filter => {
    this.selectedFilter = filter;
    this.clearCustomerSearch();

    // Check toggle state when switching categories to ensure it's properly disabled if needed
    if (filter && this.categoryHasSelections) {
      const hasSelections = this.categoryHasSelections[filter] || false;
      const toggleName = `remember${this.capitalizeFirstLetter(filter)}`;

      if (!hasSelections && this.filtersFormGroup.get(toggleName)) {
        // Make sure toggle is off and disabled if no selections
        this.filtersFormGroup.get(toggleName).setValue(false);
        // Make sure toggle is disabled if no selections, but don't change its value
        this.filtersFormGroup.get(toggleName).disable();
      } else if (
        hasSelections &&
        this.filtersFormGroup.get(toggleName) &&
        this.filtersFormGroup.get(toggleName).disabled
      ) {
        // Enable the toggle if it should be enabled
        this.filtersFormGroup.get(toggleName).enable();
      }
    }
  };

  async dismissModal(applyFilters = false) {
    if (applyFilters) {
      //comma separated analytics data for applied filters

      const selectedSellers = !!this.selectedFilterDetails?.seller
        ? `${Object.keys(this.selectedFilterDetails?.seller)
            .filter(key => this.selectedFilterDetails?.seller[key])
            .map(key => {
              const parts = key.split('_');
              return parts[parts.length - 1];
            })
            .filter(customernumber => !!customernumber)
            ?.join(',')}`
        : '';

      const selectedDeliveryMethods = !!this.selectedFilterDetails
        ?.deliveryMethod
        ? `${Object.keys(this.selectedFilterDetails?.deliveryMethod)
            .filter(key => this.selectedFilterDetails?.deliveryMethod[key])
            ?.join(',')}`
        : '';

      const selectedOrderStatus = !!this.selectedFilterDetails?.orderStatus
        ? `${Object.keys(this.selectedFilterDetails?.orderStatus)
            .filter(key => this.selectedFilterDetails?.orderStatus[key])
            ?.join(',')}`
        : '';

      const selectedCustomerIds = !!this.selectedFilterDetails?.customers
        ? Object.entries(this.selectedFilterDetails?.customers)
            .filter(([customerId, value]) => {
              if (typeof value === 'object') {
                // Check if any department has a true value
                return Object.values(value).some(val => val);
              }
              // If the value is a boolean, include if it's true
              return value === true;
            })
            .map(([customerId]) => customerId)
            .join(',')
        : '';

      //Analytics object
      this.trackSelectedFiltersDetails = {
        ...this.selectedFilterDetails,
        customers: selectedCustomerIds,
        orderStatus: selectedOrderStatus,
        deliveryDate:
          !!this.selectedFilterDetails?.deliveryDate?.start &&
          !!this.selectedFilterDetails?.deliveryDate?.end
            ? `${this.selectedFilterDetails?.deliveryDate?.start} - ${this.selectedFilterDetails?.deliveryDate?.end}`
            : '',
        deliveryMethod: selectedDeliveryMethods,
        seller: selectedSellers,
      };

      this.myOrdersAnalyticsService.trackOrderStatusPageFilterApplied(
        this.trackSelectedFiltersDetails,
      );

      // Combine all toggle states into an object to pass back
      const rememberSelections = {
        orderStatus: this.filtersFormGroup.get('rememberOrderStatus').value,
        customers: this.filtersFormGroup.get('rememberCustomers').value,
        deliveryMethod: this.filtersFormGroup.get('rememberDeliveryMethod')
          .value,
        deliveryDatePreset:
          this.filtersFormGroup.get('rememberDeliveryDatePreset')?.value ||
          false,
      };

      await this.modalController.dismiss({
        numberOfSelectedFilters: this.selectedFiltersSummary.total,
        rememberSelections: rememberSelections,
      });
    } else {
      await this.modalController.dismiss();
    }
  }

  getFirstLetter = str => str.match(/[a-zA-Z]/i);

  getFiltersKeys = () => {
    const keys = Object.keys(this.filters);
    if (
      !this.sellerFilterState?.sellerFilterEnabled ||
      !this.sellerFilterState?.showSellerTab ||
      this.sellerFilterState?.sellers?.length === 0
    ) {
      // Remove 'seller' from the keys if it exists
      return keys.filter(key => key !== 'seller');
    }
    return keys;
  };

  getObjectKeys = object => Object.keys(object);

  mapOrder(a, b) {
    return OrderStatusDetails[a]?.sortOrder - OrderStatusDetails[b]?.sortOrder;
  }

  get hasIncompleteDateRange(): boolean {
    return (
      (!!this.selectedRangeValue?.start && !this.selectedRangeValue?.end) ||
      (!this.selectedRangeValue?.start && !!this.selectedRangeValue?.end)
    );
  }

  updateSelectedFiltersSummary = value => {
    let total = 0;

    // Initialize selectedFilterDetails if not already set
    if (!this.selectedFilterDetails) {
      this.selectedFilterDetails = {
        customers: null,
        orderStatus: null,
        deliveryDate: null,
        deliveryMethod: null,
        seller: null,
      };
    }

    Object.keys(value).map(v => {
      if (v === 'deliveryDate') {
        if (value[v].start && value[v].end) {
          total += 1;

          // set the formatted summary value
          this.selectedFiltersSummary[v] = this.translateService.instant(
            'i18n.orderStatusPage.fromTo',
            {
              ...value[v],
            },
          );

          // Update deliveryDate with all its values
          this.selectedFilterDetails[v] = {
            start: value[v].start,
            end: value[v].end,
            preset: value[v].preset,
          };
        } else {
          this.selectedFiltersSummary[v] = '';
          this.selectedFilterDetails[v] = null;
        }
      } else if (!v.startsWith('remember')) {
        // Skip remember controls
        const n = this.flattenToArr(value[v]).filter(x => x).length;
        total += n;
        this.selectedFiltersSummary[v] = n;
        this.selectedFilterDetails[v] = value[v];
      }
    });
    this.selectedFiltersSummary.total = total;
  };

  // this is made to flatten the form value and identify how many filters are selected
  // it takes an objet of object (form value) and turn it into an array of the values
  flattenToArr = obj => {
    let res = [];
    if (!obj) {
      return [false];
    }
    Object.values(obj).map(o => {
      if (typeof o === 'object') {
        res = [...res, ...this.flattenToArr(o)];
      } else {
        res.push(o);
      }
    });
    return res;
  };

  selectedDateRangeChange(date: Moment) {
    if (date) {
      if (!this.selectedRangeValue?.start || this.selectedRangeValue.end) {
        this.selectedRangeValue = new DateRange<Moment>(date, null);
      } else {
        const start = this.selectedRangeValue.start;
        const end = date;
        if (end < start) {
          this.selectedRangeValue = new DateRange<Moment>(end, start);
        } else {
          this.selectedRangeValue = new DateRange<Moment>(start, end);
        }
      }
      this.updateFormDeliveryDate();
    }
  }

  updateFormDeliveryDate() {
    const deliveryDateForm = this.filtersFormGroup.get(
      'deliveryDate',
    ) as UntypedFormGroup;

    if (this.selectedRangeValue?.start && this.selectedRangeValue.end) {
      deliveryDateForm.patchValue({
        start: this.selectedRangeValue.start.format('M/D/YYYY'),
        end: this.selectedRangeValue.end.format('M/D/YYYY'),
        preset: this.activePreset,
      });
    } else {
      this.activePreset = null;
      deliveryDateForm.patchValue({
        start: '',
        end: '',
        preset: null,
      });
      // Also reset the remember toggle when clearing the date range
      if (this.filtersFormGroup.get('rememberDeliveryDatePreset')) {
        this.filtersFormGroup.get('rememberDeliveryDatePreset').setValue(false);
        this.filtersFormGroup.get('rememberDeliveryDatePreset').disable();
      }
    }
  }

  resetFilters() {
    this.filtersFormGroup.reset();
    this.selectedRangeValue = new DateRange<Moment>(null, null);
    this.activePreset = null;
    this.resetCustomersFilters();

    // Reset all remember toggles
    const categories = ['orderStatus', 'customers', 'deliveryMethod'];
    categories.forEach(category => {
      const toggleName = `remember${this.capitalizeFirstLetter(category)}`;
      if (this.filtersFormGroup.get(toggleName)) {
        this.filtersFormGroup.get(toggleName).setValue(false);
        this.filtersFormGroup.get(toggleName).disable();
      }
    });

    // Reset category selection flags
    categories.forEach(category => {
      this.categoryHasSelections[category] = false;
    });

    // Reset the delivery date preset remember toggle
    const deliveryDatePresetToggle = this.filtersFormGroup.get(
      'rememberDeliveryDatePreset',
    );
    if (deliveryDatePresetToggle) {
      deliveryDatePresetToggle.setValue(false);
      deliveryDatePresetToggle.disable();
    }
  }

  resetCustomersFilters() {
    const customersFormGroup = this.filtersFormGroup.controls[
      'customers'
    ] as UntypedFormGroup;
    if (customersFormGroup) {
      Object.keys(customersFormGroup.controls).forEach(key => {
        if (!!customersFormGroup.value[key]) {
          const departmentFormGroup = customersFormGroup.controls[
            key
          ] as UntypedFormGroup;
          Object.keys(departmentFormGroup.controls).forEach(key => {
            departmentFormGroup.controls[key].setValue(false);
          });
        } else {
          customersFormGroup.controls[key].setValue(false);
        }
      });
    }
  }

  searchCustomer(event) {
    const value = event?.target?.value || '';
    this.searchedCustomers =
      this.filters['customers']?.value?.filter(
        c =>
          !value || // no filtering if input is empty
          c.customerName.toLowerCase().includes(value.toLowerCase()) || // of filter by customer name
          c.customerNumber.toString().includes(value), // of filter by customer number
      ) || []; // return empty array if there is no customers in filters
  }

  clearCustomerSearch() {
    this.searchedCustomers = this.filters['customers']?.value || [];
  }

  /**
   * Applies the selected date preset
   */
  selectPreset(preset: DatePreset): void {
    // Update active preset
    this.activePreset = preset;

    // Get date range from preset
    const { from, to } = DatePresetService.getDateRange(preset);

    // Convert to Moment objects
    const fromMoment = moment(from);
    const toMoment = moment(to);

    // Update date range
    this.selectedRangeValue = new DateRange(fromMoment, toMoment);

    // Update form values
    this.updateFormDeliveryDate();

    // Enable save as default toggle
    this.updateSaveAsDefaultToggleState();

    // Navigate calendar to the month containing the start date
    this.navigateCalendarToStartDate(fromMoment);
  }

  /**
   * Navigates the calendar to the month containing the given date
   * This will be implemented by child components that have access to the calendar instance
   * @param date The date to navigate to
   */
  protected navigateCalendarToStartDate(date: Moment): void {
    // Store the date for future navigation if needed
    this.pendingNavigationDate = date;
  }

  /**
   * Apply any pending calendar navigation
   * Should be called by child components after the calendar is available
   */
  protected applyPendingNavigation(): void {
    if (this.pendingNavigationDate) {
      // Child components should override navigateCalendarToStartDate to use this date
      this.navigateCalendarToStartDate(this.pendingNavigationDate);
      this.pendingNavigationDate = null;
    }
  }

  /**
   * Updates the state of the Save as Default toggle based on selection mode
   */
  private updateSaveAsDefaultToggleState(): void {
    const saveAsDefaultControl = this.filtersFormGroup.get(
      'rememberDeliveryDatePreset',
    );

    if (saveAsDefaultControl) {
      if (
        this.activePreset &&
        this.selectedRangeValue?.start &&
        this.selectedRangeValue?.end
      ) {
        // Enable the toggle when a preset is active and dates are set
        saveAsDefaultControl.enable();
      } else {
        // Disable the toggle for manual selection or incomplete range, but don't change value
        saveAsDefaultControl.disable();
      }
    }
  }

  /**
   * Initialize form controls for sellers and their customers
   */
  protected initializeSellerFormControls(): void {
    if (this.filtersFormGroup) {
      const sellerGroup = this.filtersFormGroup.get('seller') as FormGroup;
      if (sellerGroup) {
        // Add form controls for each seller and their customers
        this.filteredSellers.forEach(seller => {
          if (!sellerGroup.contains(seller.id) && seller.customers) {
            seller.customers.forEach(customer => {
              const customerControlName = `${seller.id}_${customer.id}`;
              if (!sellerGroup.contains(customerControlName)) {
                sellerGroup.addControl(
                  customerControlName,
                  new FormControl(false),
                );
              }
            });
          }
        });
      }
    }
  }

  /**
   * Checks if a specific seller is expanded
   */
  isSellerExpanded(sellerId: string): boolean {
    return !!this.expandedSellers[sellerId];
  }

  /**
   * Toggles the expanded state of a seller
   */
  toggleSellerExpanded(sellerId: string): void {
    this.expandedSellers[sellerId] = !this.expandedSellers[sellerId];
  }

  /**
   * When a seller header checkbox changes, select/deselect all customers
   */
  sellerHeaderCheckboxChange(event: any, sellerId: string): void {
    const isChecked = event.detail.checked;

    // Update all customer checkboxes for this seller
    const sellerControls = this.filtersFormGroup.get('seller');
    if (sellerControls) {
      const seller = this.filteredSellers.find(s => s.id === sellerId);
      if (seller && seller.customers) {
        seller.customers.forEach(customer => {
          const customerControlName = `${sellerId}_${customer.id}`;
          const customerControl = sellerControls.get(customerControlName);
          if (customerControl) {
            customerControl.setValue(isChecked);
          }
        });
      }
    }

    // Auto-expand the seller section when checked
    if (isChecked && !this.isSellerExpanded(sellerId)) {
      this.toggleSellerExpanded(sellerId);
    }
  }

  /**
   * Checks if all seller and customer checkboxes are selected
   */
  isAllSellersSelected(): boolean {
    if (!this.filtersFormGroup?.value?.seller) {
      return false;
    }

    const sellerControls = this.filtersFormGroup.get('seller');
    if (!sellerControls) {
      return false;
    }

    // Check if all seller header checkboxes are selected
    for (const seller of this.filteredSellers) {
      // Check if all customer checkboxes for this seller are selected
      if (seller.customers) {
        for (const customer of seller.customers) {
          const customerControlName = `${seller.id}_${customer.id}`;
          const customerControl = sellerControls.get(customerControlName);
          if (customerControl && !customerControl.value) {
            return false;
          }
        }
      }
    }

    return true;
  }

  /**
   * Selects or deselects all seller and customer checkboxes
   */
  selectAllSellers(checked: boolean): void {
    const sellerControls = this.filtersFormGroup.get('seller');
    if (!sellerControls) {
      return;
    }

    // Process all sellers and their customers
    this.filteredSellers.forEach(seller => {
      // Set seller header checkboxes
      const sellerControl = sellerControls.get(seller.id);
      if (sellerControl) {
        sellerControl.setValue(checked);
      }

      // Set all customer checkboxes for this seller
      if (seller.customers) {
        seller.customers.forEach(customer => {
          const customerControlName = `${seller.id}_${customer.id}`;
          const customerControl = sellerControls.get(customerControlName);
          if (customerControl) {
            customerControl.setValue(checked);
          }
        });
      }

      // Auto-expand all sections when checked, collapse when unchecked
      this.expandedSellers[seller.id] = checked;
    });

    // Ensure any other form controls in the seller group are also set
    Object.keys((sellerControls as any).controls || {}).forEach(controlName => {
      // Double-check that we didn't miss any controls
      if (!controlName.includes('_')) {
        // This is likely a seller control
        const control = sellerControls.get(controlName);
        if (control) {
          control.setValue(checked);
        }
      } else {
        // This is likely a customer control
        const control = sellerControls.get(controlName);
        if (control) {
          control.setValue(checked);
        }
      }
    });
  }

  isSellerIndeterminate(sellerId: string): boolean {
    const form = this.filtersFormGroup.get('seller') as FormGroup;
    const customers =
      this.filteredSellers.find(s => s.id === sellerId)?.customers || [];

    const selectedCount = customers.filter(
      c => form.get(`${sellerId}_${c.id}`)?.value,
    ).length;

    return selectedCount > 0 && selectedCount < customers.length;
  }

  isSellerChecked(sellerId: string): boolean {
    const form = this.filtersFormGroup.get('seller') as FormGroup;
    if (!form) return false;

    const customers =
      this.filteredSellers.find(s => s.id === sellerId)?.customers || [];
    if (customers.length === 0) return false;

    // Check if all customer checkboxes for this seller are selected
    return customers.every(customer => {
      const customerControl = form.get(`${sellerId}_${customer.id}`);
      return customerControl?.value === true;
    });
  }

  isAnySellerSelected(): boolean {
    const form = this.filtersFormGroup.get('seller') as FormGroup;
    if (!form) return false;

    if (this.isAllSellersSelected()) return false;

    return Object.values(form.controls).some(control => control.value === true);
  }

  searchSeller(event: any) {
    const searchValue = event?.target?.value?.toLowerCase() || '';

    if (!searchValue) {
      this.searchedSellers = this.filteredSellers;
      return;
    }

    this.searchedSellers = this.filteredSellers
      .map(seller => {
        const filteredSeller = { ...seller };

        filteredSeller.customers = (seller.customers || []).filter(
          customer =>
            customer.id.toLowerCase().includes(searchValue) ||
            customer.name.toLowerCase().includes(searchValue),
        );

        return filteredSeller.customers.length > 0 ? filteredSeller : null;
      })
      .filter(Boolean);
  }

  // Clear the seller search and reset to show all sellers
  clearSellerSearch() {
    this.searchedSellers = this.filteredSellers;
  }
}
